import { ApiNames, ViewId } from "@msidentity/SISU/constants";
import { useNavigateDirection } from "@msidentity/SISU/hooks";
import { useTriggerChallenge } from "@msidentity/SISU/views/challenge/hooks/use-trigger-challenge";
import { getErrorCode } from "@msidentity/SISU/views/challenge/hooks/use-validate-challenge";
import { type SendOttResponseError } from "../../../../utilities/api-helpers";
import { SendOttErrorCode } from "../../../../utilities/api-helpers/send-ott/send-ott-request";
// TODO: Refactor strings so that we have all the error strings in one place, not in a specific view
import {
  usernameRecoveryCollectProofViewStringsFabric,
  usernameRecoveryGenericStringsFabric,
  usernameRecoveryGetAccountsForProofStringsFabric,
} from "../../fabric/username-recovery-strings-fabric";
import { useUsernameRecoveryContext } from "../../username-recovery-context";
import { UsernameRecoveryActionType } from "../../username-recovery-reducer";

const genericError = usernameRecoveryGenericStringsFabric.errorGeneric;

/**
 * Hook to handle the failure of the send ott request in the username recovery flow
 * @param currentViewId The current ViewID of the flow
 * @returns send ott failure handler
 */
export const useSendOttFailureHandler = function useSendOttFailureHandler(currentViewId: ViewId) {
  const triggerChallenge = useTriggerChallenge();
  const navigate = useNavigateDirection();
  const { dispatchStateChange } = useUsernameRecoveryContext();
  const onCollectProofView = currentViewId === ViewId.UsernameRecoveryCollect;
  const onChallengeView = currentViewId === ViewId.Challenge;

  // This method updates the error in the context and navigates to the collect proof view if not already there
  const setCollectProofError = (error: string) => {
    dispatchStateChange({
      type: UsernameRecoveryActionType.SetError,
      error,
    });

    if (!onCollectProofView) {
      navigate(currentViewId, ViewId.UsernameRecoveryCollect, false, {
        replace: currentViewId === ViewId.Challenge,
      });
    }
  };

  const clearHasPrefill = () => {
    // If there is an error with a prefilled value, we will clear it so the user can change it
    dispatchStateChange({ type: UsernameRecoveryActionType.SetHasPrefill, hasPrefill: false });
  };

  return (response: SendOttResponseError) => {
    const errorCode = getErrorCode(response);

    switch (errorCode) {
      case SendOttErrorCode.FraudBlocked:
      case SendOttErrorCode.HipCaptchaNeededOnSendOTT:
      case SendOttErrorCode.HipEnforcementNeededOnSendOTT:
      case SendOttErrorCode.HipValidationError:
        triggerChallenge({ response, apiName: ApiNames.SendOtt });
        break;
      case SendOttErrorCode.DailyLimitIDsReached:
        navigate(currentViewId, ViewId.UsernameRecoveryBlocked);
        break;
      case SendOttErrorCode.MaximumOTTDailyError:
        setCollectProofError(
          usernameRecoveryGetAccountsForProofStringsFabric.errorTriedTooManyTimes,
        );
        break;
      case SendOttErrorCode.ExpiredCredentials:
        setCollectProofError(
          onChallengeView ? genericError : usernameRecoveryGenericStringsFabric.errorExpiredCreds,
        );
        break;
      case SendOttErrorCode.InvalidPhoneFormat:
      case SendOttErrorCode.SmsNumberFormatInvalid:
        clearHasPrefill();
        setCollectProofError(
          onChallengeView
            ? genericError
            : usernameRecoveryCollectProofViewStringsFabric.invalidPhoneError,
        );
        break;
      case SendOttErrorCode.InvalidEmailFormat:
        clearHasPrefill();
        setCollectProofError(
          onChallengeView
            ? genericError
            : usernameRecoveryCollectProofViewStringsFabric.invalidEmailError,
        );
        break;
      default:
        setCollectProofError(genericError);
        break;
    }
  };
};
